import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Sparkles, TrendingUp, Wallet, ArrowRight, Users, Award, Globe2, Zap, CheckCircle } from 'lucide-react';
import Header from '../components/Header';
import ShareButtons from '../components/ShareButtons';
import { useSEO } from '../utils/seo';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const AffiliatePromoPage = () => {
  const { user } = useAuth();
  const [myCode, setMyCode] = useState(null);

  useSEO({
    title: 'Free Affiliate Programme — Earn up to 25% on every Shopperden referral',
    description: 'Sign up free in 60 seconds — no application, no credit card, no setup fee. Earn 15-25% lifetime commission on every Pulse Report, pro contact unlock and subscription a referral pays for. Instant Stripe payouts.',
    canonical: 'https://shopperden.com/affiliate',
    image: 'https://images.unsplash.com/photo-1556761175-5973dc0f32e7?w=1200&h=630&fit=crop&auto=format',
    type: 'website',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'Shopperden Affiliate Programme — Free Sign-Up',
      description: 'Free affiliate programme. Earn 15-25% lifetime commission. Instant Stripe payouts.',
      url: 'https://shopperden.com/affiliate',
    },
  });

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API_URL}/api/affiliate/me`);
        if (!cancelled) setMyCode(r.data?.referral_code);
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [user]);

  const shareUrl = myCode
    ? `https://shopperden.com/r/${myCode}`
    : 'https://shopperden.com/affiliate';
  const shareTitle = myCode
    ? `Join me on Shopperden — earn 15-25% commission on every property referral`
    : `Earn 15-25% commission — Shopperden Affiliate Programme`;

  return (
    <div className="min-h-screen bg-[#0F172A] text-white">
      <Header />

      {/* HERO — eye-catching, big numbers */}
      <section className="relative overflow-hidden pt-12 pb-20 sm:pt-20 sm:pb-28" data-testid="affiliate-hero">
        {/* Background gradient orbs */}
        <div className="absolute -top-40 -right-40 w-[500px] h-[500px] bg-[#E07A3A] opacity-20 rounded-full blur-[120px] pointer-events-none" />
        <div className="absolute -bottom-40 -left-40 w-[500px] h-[500px] bg-[#D4AF37] opacity-15 rounded-full blur-[120px] pointer-events-none" />

        <div className="max-w-5xl mx-auto px-4 relative">
          <div className="text-center">
            <div className="inline-flex items-center gap-2 bg-emerald-500/15 backdrop-blur-sm border-2 border-emerald-400/40 px-4 py-2 rounded-full mb-6" data-testid="affiliate-badge">
              <Sparkles className="h-4 w-4 text-emerald-300" />
              <span className="text-xs uppercase tracking-[0.2em] font-bold text-emerald-200">
                100% Free to join · No application · No setup fee
              </span>
            </div>

            <h1 className="font-['Playfair_Display'] text-5xl sm:text-7xl lg:text-8xl font-semibold mb-6 leading-[0.95]">
              Earn up to{' '}
              <span className="bg-gradient-to-r from-[#E07A3A] via-[#D4AF37] to-[#E07A3A] bg-clip-text text-transparent">
                25%
              </span>
              <br />
              <span className="text-3xl sm:text-5xl lg:text-6xl text-white/80 font-normal italic">on every referral</span>
            </h1>

            <p className="text-lg sm:text-xl text-white/70 max-w-2xl mx-auto leading-relaxed mb-10">
              Share Shopperden with anyone who'd benefit. When they pay for anything — Pulse Reports, pro contacts, partner subscriptions — you earn a commission for life. Paid monthly via bank transfer.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-10">
              {user ? (
                <Link
                  to="/buyer?tab=portfolio"
                  className="bg-emerald-500 hover:bg-emerald-600 text-white px-8 py-4 text-sm uppercase tracking-[0.25em] font-bold rounded transition-all hover:scale-105 flex items-center gap-2 shadow-lg shadow-emerald-500/30"
                  data-testid="affiliate-cta-dashboard"
                >
                  Get my share link <ArrowRight className="h-4 w-4" />
                </Link>
              ) : (
                <Link
                  to="/register"
                  className="bg-emerald-500 hover:bg-emerald-600 text-white px-8 py-4 text-sm uppercase tracking-[0.25em] font-bold rounded transition-all hover:scale-105 flex items-center gap-2 shadow-lg shadow-emerald-500/30"
                  data-testid="affiliate-cta-signup"
                >
                  Sign up FREE — start earning <ArrowRight className="h-4 w-4" />
                </Link>
              )}
              <Link
                to="#how-it-works"
                className="text-white/80 hover:text-white text-sm uppercase tracking-[0.25em] font-semibold underline underline-offset-4"
                data-testid="affiliate-cta-how"
              >
                How it works
              </Link>
            </div>
            {!user && (
              <p className="text-[11px] text-white/40 mb-6">
                Free forever · No credit card · 60-second sign-up
              </p>
            )}

            {/* One-click share — featured prominently */}
            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6 max-w-2xl mx-auto" data-testid="affiliate-share-block">
              <p className="text-[10px] tracking-[0.3em] uppercase text-[#D4AF37] font-bold mb-3">
                One-click share — start earning today
              </p>
              <ShareButtons
                url={shareUrl}
                title={shareTitle}
                size="md"
                label=""
                dataTestId="affiliate-hero-share"
              />
              {myCode && (
                <p className="text-xs text-white/50 mt-3 font-mono break-all">
                  Your link: <span className="text-white/80">{shareUrl}</span>
                </p>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* TIER CARDS — visually striking */}
      <section className="py-16 sm:py-24 bg-white text-[#0F172A]">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <p className="text-[10px] tracking-[0.3em] uppercase text-[#E07A3A] font-bold mb-3">Commission tiers</p>
            <h2 className="font-['Playfair_Display'] text-3xl sm:text-5xl text-[#0F172A] mb-4">
              Your role decides your rate
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">Higher rates for those with the network — but every Shopperden user earns.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Agent — premium */}
            <div className="relative bg-gradient-to-br from-[#0F172A] to-[#1E293B] text-white p-8 rounded-xl border-2 border-[#D4AF37] hover:scale-[1.02] transition-transform" data-testid="tier-agent">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#D4AF37] text-[#0F172A] text-[10px] uppercase tracking-[0.2em] font-bold px-3 py-1 rounded">
                ⭐ Top Tier
              </div>
              <Award className="h-8 w-8 text-[#D4AF37] mb-4" />
              <p className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] mb-1">Agent</p>
              <p className="font-['Playfair_Display'] text-6xl font-bold mb-3">25%</p>
              <p className="text-white/70 text-sm leading-relaxed">
                Lifetime commission on every paid transaction by any user you refer. Best for estate agents with active buyer/seller networks.
              </p>
            </div>

            {/* Pro */}
            <div className="bg-[#FEF3EC] p-8 rounded-xl hover:scale-[1.02] transition-transform" data-testid="tier-pro">
              <Users className="h-8 w-8 text-[#E07A3A] mb-4" />
              <p className="text-[10px] uppercase tracking-[0.3em] text-[#E07A3A] mb-1">Professional</p>
              <p className="font-['Playfair_Display'] text-6xl font-bold text-[#0F172A] mb-3">20%</p>
              <p className="text-slate-700 text-sm leading-relaxed">
                Solicitors, mortgage brokers, surveyors — refer clients and earn on their full Shopperden lifetime spend.
              </p>
            </div>

            {/* Buyer/Tenant/Seller */}
            <div className="bg-[#F0FDF4] p-8 rounded-xl hover:scale-[1.02] transition-transform" data-testid="tier-buyer">
              <Globe2 className="h-8 w-8 text-emerald-600 mb-4" />
              <p className="text-[10px] uppercase tracking-[0.3em] text-emerald-700 mb-1">Buyer · Tenant · Seller</p>
              <p className="font-['Playfair_Display'] text-6xl font-bold text-[#0F172A] mb-3">15%</p>
              <p className="text-slate-700 text-sm leading-relaxed">
                Share with friends and family looking to buy, rent or sell. Pure passive income — paid on every transaction they make.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-16 sm:py-24 bg-[#F8FAFC] text-[#0F172A]" id="how-it-works">
        <div className="max-w-5xl mx-auto px-4">
          <div className="text-center mb-12">
            <p className="text-[10px] tracking-[0.3em] uppercase text-[#E07A3A] font-bold mb-3">How it works</p>
            <h2 className="font-['Playfair_Display'] text-3xl sm:text-5xl text-[#0F172A] mb-4">3 steps to first payout</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { n: '1', icon: <Sparkles className="h-6 w-6" />, title: 'Grab your share link', desc: 'Every Shopperden account comes with a unique referral code — no application or approval needed. Find it in your dashboard.' },
              { n: '2', icon: <Zap className="h-6 w-6" />, title: 'Share anywhere — one click', desc: 'Facebook, X, LinkedIn, WhatsApp, Telegram, email, or copy-paste anywhere. The link auto-attributes every sign-up.' },
              { n: '3', icon: <Wallet className="h-6 w-6" />, title: 'Get paid monthly', desc: 'Commission credits in real-time when your referrals pay. Once your balance crosses £50 / ₹2,500 / C$75, request a bank payout in one click.' },
            ].map((s) => (
              <div key={s.n} className="bg-white border border-slate-200 rounded-xl p-6 relative" data-testid={`how-step-${s.n}`}>
                <div className="absolute -top-4 -left-2 bg-[#E07A3A] text-white w-10 h-10 rounded-full flex items-center justify-center font-bold text-lg">{s.n}</div>
                <div className="text-[#E07A3A] mb-3 mt-2">{s.icon}</div>
                <h3 className="font-['Playfair_Display'] text-xl mb-2">{s.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHAT YOU EARN ON */}
      <section className="py-16 sm:py-24 bg-white text-[#0F172A]">
        <div className="max-w-5xl mx-auto px-4">
          <div className="text-center mb-12">
            <p className="text-[10px] tracking-[0.3em] uppercase text-[#E07A3A] font-bold mb-3">Earn on everything</p>
            <h2 className="font-['Playfair_Display'] text-3xl sm:text-5xl mb-4">Every Shopperden purchase pays you</h2>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            {[
              { name: 'Pulse Reports', price: '£49' },
              { name: 'Pro Contacts', price: '£15.52' },
              { name: 'Vault Unlock', price: '£25' },
              { name: 'Pulse Buyer Sub', price: '£29/mo' },
              { name: 'Verified Partner', price: '£199' },
              { name: 'Power Partner', price: '£499' },
              { name: 'Pulse Seller Boost', price: '£15' },
              { name: 'Growth Analysis', price: '£49' },
              { name: 'Agent Subscription', price: 'tiered' },
              { name: 'NRI Pulse Lead Magnet', price: '£49' },
            ].map((p) => (
              <div key={p.name} className="bg-[#F8FAFC] border border-slate-200 rounded p-4 text-center hover:border-[#E07A3A] transition-colors" data-testid={`earn-${p.name.toLowerCase().replace(/[^a-z]/g, '-')}`}>
                <CheckCircle className="h-4 w-4 text-emerald-600 mx-auto mb-2" />
                <p className="text-xs font-semibold text-[#0F172A]">{p.name}</p>
                <p className="text-[10px] text-slate-500 mt-1">{p.price}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* BIG SHARE CTA */}
      <section className="py-16 sm:py-24 bg-gradient-to-br from-[#E07A3A] via-[#D4AF37] to-[#E07A3A] text-[#0F172A]">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="font-['Playfair_Display'] text-4xl sm:text-6xl font-bold mb-4">Tell one friend.</h2>
          <p className="text-xl text-[#0F172A]/70 mb-10 italic">Watch the commissions roll in for life.</p>

          <div className="bg-white rounded-xl p-6 sm:p-8 shadow-2xl" data-testid="affiliate-final-share">
            <p className="text-[10px] tracking-[0.3em] uppercase text-[#E07A3A] font-bold mb-4">Share now in one click</p>
            <ShareButtons
              url={shareUrl}
              title={shareTitle}
              size="md"
              label=""
              dataTestId="affiliate-final-share-row"
            />
          </div>

          {!user && (
            <Link
              to="/register"
              className="inline-flex items-center gap-2 bg-[#0F172A] hover:bg-black text-white px-10 py-5 mt-10 text-sm uppercase tracking-[0.3em] font-bold rounded transition-all hover:scale-105"
              data-testid="affiliate-bottom-signup"
            >
              Sign up & get my link <ArrowRight className="h-4 w-4" />
            </Link>
          )}
        </div>
      </section>

      {/* Trust footer */}
      <section className="py-12 bg-[#0F172A] text-white/60 text-center">
        <p className="text-xs tracking-[0.2em] uppercase">
          Live across <span className="text-[#D4AF37]">United Kingdom · India · Canada</span> · 1,100+ aggregate listings · 561 verified pros
        </p>
      </section>
    </div>
  );
};

export default AffiliatePromoPage;
